import { ICard, ICardToolbarAction, EventDispatcher } from "./Card";
import * as MathUtil from "./MathUtil";
import { DeckUtil } from "./DeckUtil";
import { CardUtil } from "./CardUtil";

export interface IDeck {
    name?: string;
    icon?: string;
    cards: ICard[];
}
export enum NavigateDirection {
    Up,
    Down,
    Left,
    Right,
    Delete,
    Enter
}
export interface IDeckToolbarSettings {
    flip?: boolean;
    zoom?: boolean;
    shuffle?: boolean;
    navigation?: boolean;
    navigationAllowedOnFaceDownCards?: boolean;
    exhaust?: boolean;
    addCard?: boolean;
    removeTopCardFromPlay?: boolean;
    randomCardToTop?: boolean;
    edit?: boolean;
    align?: string;
    navigate?: (direction: NavigateDirection) => boolean;
    actions?: ICardToolbarAction[];
}
export class Deck {
    toolbarSettings: IDeckToolbarSettings;
    private deck: IDeck;
    constructor(toolbarSettings: IDeckToolbarSettings) {

        this.toolbarSettings = toolbarSettings || {};
    }
    private events = new EventDispatcher<string>();
    private static readonly onChangeEvent = "onchange";
    static readonly onChangeShuffleStart = "shuffleStart";
    static readonly onChangeShuffleEnd = "shuffleEnd";
    static readonly onChangeToggleZoom = "toggleZoom";
    static readonly onShowAddCardSelector = "showAddCardSelector";
    static readonly onRemoveTopCardFromPlay = "removeTopCardFromPlay";
    static readonly onEdit = "onEdit";
    setDeck(deck: IDeck) {
        this.deck = deck;
    }
    private triggerOnChange(arg: string) {
        this.events.trigger(Deck.onChangeEvent, arg);
    }
    onChange(handler: (arg: string) => void): void {
        this.events.addEventListener(Deck.onChangeEvent, handler);
    }

    removeOnChange(handler: (arg: string) => void): void {
        this.events.removeEventListener(Deck.onChangeEvent, handler);
    }
    navigate(direction: NavigateDirection): boolean {
        if (this.toolbarSettings.navigate) {
            return this.toolbarSettings.navigate(direction);
        } else {
            switch (direction) {
                case NavigateDirection.Up:
                case NavigateDirection.Down:
                    return this.flipCard();
                case NavigateDirection.Left:
                    return this.navigateBack();
                case NavigateDirection.Right:
                    return this.navigateForward();
            }
            return false;
        }
    }

    flipCard(): boolean {
        if (!this.toolbarSettings.flip) return false;
        const topCard = this.topCard();
        if (topCard.values && topCard.values["flipDisabled"])
            return false;
        if (topCard.flipTransformsTo) {
            const f = topCard.flipTransformsTo;
            topCard.pack = f.pack;
            topCard.packNumber = f.packNumber;
            topCard.name = f.name;
            topCard.flipTransformsTo = f.flipTransformsTo;
            topCard.set = f.set;
            topCard.subType = f.subType;
            topCard.subType2 = f.subType2;
            topCard.type = f.type;
            topCard.victoryPoints = f.victoryPoints;
            topCard.values = f.values;
            topCard.tokens = null;
            if (!topCard.values) {
                topCard.values = [];
            }
        } else {
            topCard.faceUp = !topCard.faceUp;
        }
        this.triggerOnChange("flipCard");
        return true;
    }
    exhaustCard() {
        if (!this.toolbarSettings.exhaust) return;
        const topCard = this.topCard();
        topCard.exhausted = !topCard.exhausted;
        this.triggerOnChange("exhaustCard");
    }
    topCard(): ICard {
        if (this.deck && this.deck.cards && this.deck.cards.length)
            return this.deck.cards[0];
        return null;
    }
    topCardExists(): boolean {
        const topCard = this.topCard();
        return !!topCard;
    }

    flipTopCardIfNeeded(): boolean {
        const topCard = this.topCard();
        if (!topCard) return false;
        if (!this.toolbarSettings.navigationAllowedOnFaceDownCards && !topCard.faceUp) {
            topCard.faceUp = true;
            this.triggerOnChange("flipCard");
            return true;
        }
        return false;
    }

    navigateBack(): boolean {
        if (!this.toolbarSettings.navigation) return false;
        if (!this.topCardExists()) return false;
        if (this.flipTopCardIfNeeded()) return true;
        if (this.deck.cards.length < 2) return false;
        const cards = this.deck.cards;
        const bottomCard = cards.splice(cards.length - 1, 1)[0];
        bottomCard.faceUp = true;
        cards.splice(0, 0, bottomCard);
        this.triggerOnChange("navigateBack");
        return true;
    }

    navigateForward(): boolean {
        if (!this.toolbarSettings.navigation) return false;
        if (!this.topCardExists()) return false;
        if (this.flipTopCardIfNeeded()) return true;
        if (this.deck.cards.length < 2) return false;
        const cards = this.deck.cards;
        let topCard = cards.splice(0, 1)[0];
        cards.splice(cards.length, 0, topCard);
        topCard = this.topCard();
        topCard.faceUp = true;
        this.triggerOnChange("navigateForward");
        return true;
    }
    shuffleCards() {
        if (!this.toolbarSettings.shuffle) return;
        this.triggerOnChange(Deck.onChangeShuffleStart);
        setTimeout(() => {
            CardUtil.flip(this.deck.cards, false);
            CardUtil.shuffle(this.deck.cards);
            this.triggerOnChange("shuffleCards");
            this.triggerOnChange(Deck.onChangeShuffleEnd);
        }, 1000);
    }
    randomCardToTop() {
        if (!this.toolbarSettings.randomCardToTop) return;
        this.triggerOnChange(Deck.onChangeShuffleStart);
        setTimeout(() => {
            const cards = this.deck.cards;
            const randomChoiceIndex = MathUtil.MathUtil.random(0, cards.length - 1);
            const card = cards.splice(randomChoiceIndex, 1)[0];
            cards.splice(0, 0, card);
            this.triggerOnChange("randomCardToTop");
            this.triggerOnChange(Deck.onChangeShuffleEnd);
        }, 1000);
    }
    toggleZoom(): boolean {
        if (!this.toolbarSettings.zoom) return false;
        this.triggerOnChange(Deck.onChangeToggleZoom);
        return true;
    }
    edit() {
        if (!this.toolbarSettings.edit) return;
        this.triggerOnChange(Deck.onEdit);
    }
    hasCard() {
        return DeckUtil.hasCard(this.deck);
    }
    noOfCards() {
        return this.deck && this.deck.cards ? this.deck.cards.length : 0;
    }
    showAddCardSelector() {
        this.triggerOnChange(Deck.onShowAddCardSelector);
        return true;
    }
    removeTopCardFromPlay() {
        DeckUtil.removeTopCard(this.deck);
        this.triggerOnChange(Deck.onRemoveTopCardFromPlay);
        return true;
    }
}